import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from "@headlessui/react";
import {
  CheckIcon,
  ChevronUpDownIcon
} from "@heroicons/react/24/outline";
import { useEffect, useMemo, useRef, useState } from "react";


interface YearComboboxProps {
  onYearSelect: (year: string) => void;
  currentValue?: string;
  label?: string;
  // Inclusive lower / upper bounds for the generated year list.
  minYear?: number;
  maxYear?: number;
  placeholder?: string;
  disabled?: boolean;
}

const DEFAULT_MIN_YEAR = 1980;

export function YearCombobox({
  onYearSelect,
  currentValue,
  label,
  minYear = DEFAULT_MIN_YEAR,
  maxYear,
  placeholder = "Search years...",
  disabled = false,
}: YearComboboxProps) {
  const [selectedYear, setSelectedYear] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const previousBoundsRef = useRef<string>(`${minYear}|${maxYear ?? ""}`);

  const years = useMemo(() => {
    const upper = maxYear ?? new Date().getFullYear() + 1;
    const lower = Math.min(minYear, upper);

    const list: string[] = [];
    for (let y = upper; y >= lower; y--) {
      list.push(y.toString());
    }

    if (!searchTerm) return list;

    const needle = searchTerm.toLowerCase();
    return list.filter((y) => y.toLowerCase().includes(needle));
  }, [minYear, maxYear, searchTerm]);

  // Keep the visible value in sync with the form's source of truth and reset
  // the local selection if the bounds change in a way that would invalidate it.
  useEffect(() => {
    const boundsKey = `${minYear}|${maxYear ?? ""}`;
    const boundsChanged = previousBoundsRef.current !== boundsKey;
    previousBoundsRef.current = boundsKey;

    if (currentValue === undefined) return;

    if (currentValue === "") {
      setSelectedYear(null);
      setSearchTerm("");
      return;
    }

    const numeric = Number(currentValue);
    const upper = maxYear ?? new Date().getFullYear() + 1;
    const outOfRange = !Number.isNaN(numeric) && (numeric < minYear || numeric > upper);

    if (boundsChanged && outOfRange) {
      setSelectedYear(null);
      setSearchTerm("");
      onYearSelect("");
      return;
    }

    if (currentValue !== selectedYear) {
      setSelectedYear(currentValue);
      setSearchTerm(currentValue);
    }
  }, [currentValue, minYear, maxYear, selectedYear, onYearSelect]);

  return (
    <div>
      {label &&
        <label>
          <span className="text-sm font-medium text-black">{label}</span>
        </label>
      }
      <Combobox
        as="div"
        value={selectedYear ?? null}
        onChange={(year: string) => {
          setSelectedYear(year);
          onYearSelect(year);
        }}
      >
        <div className="relative mt-1">
          <ComboboxInput
            className="w-full py-2 pl-3 pr-10 text-sm leading-5 text-gray-900 border-1 border-gray-400 rounded-md disabled:bg-gray-100"
            onChange={(event) => setSearchTerm(event.target.value)}
            displayValue={(year: string) => year || searchTerm}
            placeholder={placeholder}
            autoComplete="off"
            disabled={disabled}
            inputMode="numeric"
          />
          <ComboboxButton className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
            <ChevronUpDownIcon
              className="size-5 text-gray-400"
              aria-hidden="true"
            />
          </ComboboxButton>

          <ComboboxOptions
            className="absolute z-30 mt-1 max-h-60 w-full overflow-auto bg-white py-1 text-base rounded-md ring-1 ring-gray-400 ring-opacity-5 focus:outline-none sm:text-sm"
          >
            {years && years.length > 0 ? (
              years.slice(0, 100).map((year) => (
                <ComboboxOption
                  key={year}
                  value={year}
                  className={({ active }) =>
                    `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? 'bg-primary-600 text-white' : 'text-gray-900'
                    }`
                  }
                >
                  {({ selected, active }) => (
                    <>
                      <span
                        className={`block truncate ${selected ? 'font-medium' : 'font-normal'
                          }`}
                      >
                        {year}
                      </span>
                      {selected ? (
                        <span
                          className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-primary-600'
                            }`}
                        >
                          <CheckIcon className="h-5 w-5" aria-hidden="true" />
                        </span>
                      ) : null}
                    </>
                  )}
                </ComboboxOption>
              ))
            ) : (
              <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                Nothing found.
              </div>
            )}
          </ComboboxOptions>
        </div>
      </Combobox >
    </div >
  );
}
